import type { Grid, Token, Direction, MoveResult, Pos } from "../types.ts";
import { posToKey, hasWall, getAdjacentPos, getCell, isInBounds, cloneTokens, checkWin } from "./grid.ts";

export function applyDirection(grid: Grid, tokens: Token[], dir: Direction): MoveResult {
  const newTokens = cloneTokens(tokens);
  const alive = newTokens.filter((t) => !t.lost);

  // Sort tokens by processing order: furthest in movement direction goes first
  sortByDirection(alive, dir);

  // Track occupied cells as tokens settle
  const occupied = new Set<string>();

  for (const token of alive) {
    let current = token.pos;
    let teleported = false;

    // eslint-disable-next-line no-constant-condition
    while (true) {
      // Wall on current cell blocks exit
      if (hasWall(grid, current, dir)) break;

      const next = getAdjacentPos(current, dir);

      // Out of bounds (shouldn't happen with edge walls, but safety)
      if (!isInBounds(grid, next)) break;

      // Another settled token blocks entry
      if (occupied.has(posToKey(next))) break;

      // Move to next
      current = next;

      // Check for hole
      const cell = getCell(grid, current)!;
      if (cell.isHole) {
        token.pos = current;
        token.lost = true;
        break;
      }

      // Check for teleporter (only use once per slide to prevent ping-pong)
      if (!teleported && cell.teleporterId != null) {
        const pair = grid.teleporters.find((t) => t.id === cell.teleporterId);
        if (pair) {
          const exitPos =
            posToKey(pair.a) === posToKey(current) ? pair.b : pair.a;

          // If exit is occupied by a settled token, stay at entrance
          if (occupied.has(posToKey(exitPos))) break;

          current = { ...exitPos };
          teleported = true;

          // Check if exit is a hole
          const exitCell = getCell(grid, current)!;
          if (exitCell.isHole) {
            token.pos = current;
            token.lost = true;
            break;
          }

          // Continue sliding from exit
          continue;
        }
      }

      // Keep sliding
    }

    if (!token.lost) {
      token.pos = current;
      occupied.add(posToKey(current));
    }
  }

  const anyLost = newTokens.some((t) => t.lost);
  const won = !anyLost && checkWin(grid, newTokens);

  return { tokens: newTokens, anyLost, won };
}

function sortByDirection(tokens: Token[], dir: Direction): void {
  switch (dir) {
    case "right":
      tokens.sort((a, b) => b.pos.col - a.pos.col);
      break;
    case "left":
      tokens.sort((a, b) => a.pos.col - b.pos.col);
      break;
    case "down":
      tokens.sort((a, b) => b.pos.row - a.pos.row);
      break;
    case "up":
      tokens.sort((a, b) => a.pos.row - b.pos.row);
      break;
  }
}
